import { Box, NumberInput, Select } from "@mantine/core";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { LateFeeApi } from "../../apis/late-fee";
import { useLateFeeConfig } from "../../hooks/use-late-fee-config";
import { message } from "../../utils/Message";
import { LateFeeExampleCard } from "./late-fee-example-card";
import { countChanges, MasterCard, OffCard, SaveBar, SectionCard, SettingRow, settingsClasses, SwitchRow, unitProps } from "./settings-ui";
import type { SettingsTabProps } from "./settings-ui";

interface ILateFeeConfig {
  lateFeeEnabled: boolean;
  feeCalculationType: string | null;
  dailyPenaltyRate: number | string;
  monthlyPenaltyRate: number | string;
  flatFeeAmount: number | string;
  gracePeriodDays: number | string;
  maxPenaltyRate: number | string;
  maxPenaltyAmount: number | string;
  minimumPenaltyAmount: number | string;
  applicationMethod: string | null;
  allowPerBuyerOverride: boolean;
  useEscalatingRates: boolean;
}

const initialConfig: ILateFeeConfig = {
  lateFeeEnabled: false,
  feeCalculationType: null,
  dailyPenaltyRate: "",
  monthlyPenaltyRate: "",
  flatFeeAmount: "",
  gracePeriodDays: "",
  maxPenaltyRate: "",
  maxPenaltyAmount: "",
  minimumPenaltyAmount: "",
  applicationMethod: null,
  allowPerBuyerOverride: false,
  useEscalatingRates: false,
};

const fromServer = (data: any): ILateFeeConfig => ({
  lateFeeEnabled: data.lateFeeEnabled ?? false,
  feeCalculationType: data.feeCalculationType ?? null,
  dailyPenaltyRate: data.dailyPenaltyRate ?? "",
  monthlyPenaltyRate: data.monthlyPenaltyRate ?? "",
  flatFeeAmount: data.flatFeeAmount ?? "",
  gracePeriodDays: data.gracePeriodDays ?? "",
  maxPenaltyRate: data.maxPenaltyRate ?? "",
  maxPenaltyAmount: data.maxPenaltyAmount ?? "",
  minimumPenaltyAmount: data.minimumPenaltyAmount ?? "",
  applicationMethod: data.applicationMethod ?? null,
  allowPerBuyerOverride: data.allowPerBuyerOverride ?? false,
  useEscalatingRates: data.useEscalatingRates ?? false,
});

export function LateFeeConfig({ asideTarget, onDirtyChange }: SettingsTabProps) {
  const { t } = useTranslation("settings");
  const [saving, setSaving] = useState(false);

  const feeCalculationOptions = [
    { value: "DAILY_PERCENTAGE", label: t("lateFee.calcType.daily") },
    { value: "MONTHLY_PERCENTAGE", label: t("lateFee.calcType.monthly") },
    { value: "COMPOUND_DAILY", label: t("lateFee.calcType.compound") },
    { value: "FLAT_FEE", label: t("lateFee.calcType.flat") },
    { value: "ESCALATING", label: t("lateFee.calcType.escalating") },
  ];

  const applicationMethodOptions = [
    { value: "ADD_TO_INVOICE", label: t("lateFee.appMethod.addToInvoice") },
    { value: "SEPARATE_INVOICE", label: t("lateFee.appMethod.separate") },
    { value: "BALANCE_ADJUSTMENT", label: t("lateFee.appMethod.balance") },
  ];
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [config, setConfig] = useState<ILateFeeConfig>(initialConfig);
  const [saved, setSaved] = useState<ILateFeeConfig>(initialConfig);

  const { data, isLoading, mutate } = useLateFeeConfig();

  useEffect(() => {
    const server = (data as any)?.config;
    if (!server) return;
    const next = fromServer(server);
    setConfig(next);
    setSaved(next);
  }, [data]);

  const handleChange = (field: keyof ILateFeeConfig, value: any) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const showDailyRate = config.feeCalculationType === "DAILY_PERCENTAGE" || config.feeCalculationType === "COMPOUND_DAILY";
  const showMonthlyRate = config.feeCalculationType === "MONTHLY_PERCENTAGE";
  const showFlatFee = config.feeCalculationType === "FLAT_FEE";

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!config.lateFeeEnabled) return true;
    if (!config.feeCalculationType) newErrors.feeCalculationType = t("lateFee.calcType.required");
    if (showDailyRate && (!config.dailyPenaltyRate || config.dailyPenaltyRate === "")) newErrors.dailyPenaltyRate = t("lateFee.dailyRate.required");
    if (showMonthlyRate && (!config.monthlyPenaltyRate || config.monthlyPenaltyRate === "")) newErrors.monthlyPenaltyRate = t("lateFee.monthlyRate.required");
    if (showFlatFee && (!config.flatFeeAmount || config.flatFeeAmount === "")) newErrors.flatFeeAmount = t("lateFee.flatFee.required");
    if (!config.applicationMethod) newErrors.applicationMethod = t("lateFee.appMethod.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { message.error(t("lateFee.validationError")); return; }
    try {
      setSaving(true);
      // Drop empty-string / null fields so the backend Joi schema applies its
      // defaults/optionals instead of rejecting "" as an invalid number.
      const payload = Object.fromEntries(
        Object.entries(config).filter(([, v]) => v !== "" && v !== null)
      );
      await LateFeeApi.updateConfig(payload);
      setSaved(config);
      await mutate();
      message.success(t("lateFee.saveSuccess"));
    } catch (err: any) {
      message.error(err?.message || t("lateFee.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const changes = countChanges(config, saved);
  useEffect(() => onDirtyChange(changes), [changes, onDirtyChange]);
  useEffect(() => () => onDirtyChange(0), [onDirtyChange]);

  const field = { size: "sm", radius: "md" } as const;

  return (
    <Box className={isLoading ? settingsClasses.loading : undefined}>
      <Box className={settingsClasses.main}>
        {!config.lateFeeEnabled ? (
          <OffCard
            title={t("lateFee.off.title")}
            description={t("lateFee.off.description")}
            enableLabel={t("lateFee.off.enable")}
            onEnable={() => handleChange("lateFeeEnabled", true)}
            features={[
              { title: t("lateFee.off.featureDaily"), description: t("lateFee.off.featureDailyText") },
              { title: t("lateFee.off.featureGrace"), description: t("lateFee.off.featureGraceText") },
              { title: t("lateFee.off.featureCap"), description: t("lateFee.off.featureCapText") },
            ]}
          />
        ) : (
          <>
            <MasterCard
              title={t("lateFee.master.title")}
              subtitle={t("lateFee.master.subtitle")}
              checked={config.lateFeeEnabled}
              onChange={(v) => handleChange("lateFeeEnabled", v)}
            />

            <SectionCard title={t("lateFee.section.calculation")} subtitle={t("lateFee.section.calculationSubtitle")}>
              <SettingRow label={t("lateFee.calcType.label")} description={t("lateFee.calcType.description")} required>
                <Select
                  aria-label={t("lateFee.calcType.label")}
                  placeholder={t("lateFee.calcType.placeholder")}
                  data={feeCalculationOptions}
                  value={config.feeCalculationType}
                  onChange={(v) => handleChange("feeCalculationType", v)}
                  clearable
                  error={errors.feeCalculationType}
                  {...field}
                />
              </SettingRow>
              {showDailyRate && (
                <SettingRow label={t("lateFee.dailyRate.label")} description={t("lateFee.dailyRate.description")} required>
                  <NumberInput
                    aria-label={t("lateFee.dailyRate.label")}
                    placeholder={t("lateFee.dailyRate.placeholder")}
                    value={config.dailyPenaltyRate}
                    onChange={(v) => handleChange("dailyPenaltyRate", v)}
                    min={0} max={100} decimalScale={4}
                    error={errors.dailyPenaltyRate}
                    {...unitProps("%")}
                    {...field}
                  />
                </SettingRow>
              )}
              {showMonthlyRate && (
                <SettingRow label={t("lateFee.monthlyRate.label")} description={t("lateFee.monthlyRate.description")} required>
                  <NumberInput
                    aria-label={t("lateFee.monthlyRate.label")}
                    placeholder={t("lateFee.monthlyRate.placeholder")}
                    value={config.monthlyPenaltyRate}
                    onChange={(v) => handleChange("monthlyPenaltyRate", v)}
                    min={0} max={100} decimalScale={4}
                    error={errors.monthlyPenaltyRate}
                    {...unitProps("%")}
                    {...field}
                  />
                </SettingRow>
              )}
              {showFlatFee && (
                <SettingRow label={t("lateFee.flatFee.label")} description={t("lateFee.flatFee.description")} required>
                  <NumberInput
                    aria-label={t("lateFee.flatFee.label")}
                    placeholder={t("lateFee.flatFee.placeholder")}
                    value={config.flatFeeAmount}
                    onChange={(v) => handleChange("flatFeeAmount", v)}
                    min={0} thousandSeparator=","
                    error={errors.flatFeeAmount}
                    {...unitProps("₮")}
                    {...field}
                  />
                </SettingRow>
              )}
              <SettingRow label={t("lateFee.gracePeriod.label")} description={t("lateFee.gracePeriod.description")}>
                <NumberInput
                  aria-label={t("lateFee.gracePeriod.label")}
                  placeholder={t("lateFee.gracePeriod.placeholder")}
                  value={config.gracePeriodDays}
                  onChange={(v) => handleChange("gracePeriodDays", v)}
                  min={0}
                  {...unitProps(t("unit.days"))}
                  {...field}
                />
              </SettingRow>
            </SectionCard>

            <SectionCard title={t("lateFee.section.limits")} subtitle={t("lateFee.section.limitsSubtitle")}>
              <SettingRow label={t("lateFee.maxRate.label")} description={t("lateFee.maxRate.description")}>
                <NumberInput
                  aria-label={t("lateFee.maxRate.label")}
                  placeholder={t("lateFee.maxRate.placeholder")}
                  value={config.maxPenaltyRate}
                  onChange={(v) => handleChange("maxPenaltyRate", v)}
                  min={0} max={100}
                  {...unitProps("%")}
                  {...field}
                />
              </SettingRow>
              <SettingRow label={t("lateFee.maxAmount.label")} description={t("lateFee.maxAmount.description")}>
                <NumberInput
                  aria-label={t("lateFee.maxAmount.label")}
                  placeholder={t("lateFee.maxAmount.placeholder")}
                  value={config.maxPenaltyAmount}
                  onChange={(v) => handleChange("maxPenaltyAmount", v)}
                  min={0} thousandSeparator=","
                  {...unitProps("₮")}
                  {...field}
                />
              </SettingRow>
              <SettingRow label={t("lateFee.minAmount.label")} description={t("lateFee.minAmount.description")}>
                <NumberInput
                  aria-label={t("lateFee.minAmount.label")}
                  placeholder={t("lateFee.minAmount.placeholder")}
                  value={config.minimumPenaltyAmount}
                  onChange={(v) => handleChange("minimumPenaltyAmount", v)}
                  min={0} thousandSeparator=","
                  {...unitProps("₮")}
                  {...field}
                />
              </SettingRow>
            </SectionCard>

            <SectionCard title={t("lateFee.section.additional")} subtitle={t("lateFee.section.additionalSubtitle")}>
              <SettingRow label={t("lateFee.appMethod.label")} description={t("lateFee.appMethod.description")} required>
                <Select
                  aria-label={t("lateFee.appMethod.label")}
                  placeholder={t("lateFee.appMethod.placeholder")}
                  data={applicationMethodOptions}
                  value={config.applicationMethod}
                  onChange={(v) => handleChange("applicationMethod", v)}
                  clearable
                  error={errors.applicationMethod}
                  {...field}
                />
              </SettingRow>
              <SwitchRow
                label={t("lateFee.buyerOverride.label")}
                description={t("lateFee.buyerOverride.description")}
                checked={config.allowPerBuyerOverride}
                onChange={(v) => handleChange("allowPerBuyerOverride", v)}
              />
              <SwitchRow
                label={t("lateFee.escalatingRates.label")}
                description={t("lateFee.escalatingRates.description")}
                checked={config.useEscalatingRates}
                onChange={(v) => handleChange("useEscalatingRates", v)}
              />
            </SectionCard>
          </>
        )}

        <SaveBar count={changes} saving={saving} onSave={handleSave} onCancel={() => { setConfig(saved); setErrors({}); }} />
      </Box>
      {config.lateFeeEnabled && asideTarget && createPortal(<LateFeeExampleCard config={config} />, asideTarget)}
    </Box>
  );
}
